/**
 * Wunschliste (Stufe 1) – eigenstaendige Logik neben theme.js.
 *
 * - Ablage im localStorage, Schluessel `acl_wishlist`, hoechstens 100 Eintraege.
 *   Eintrag: { handle, productId, variantId|null, addedAt, price, color }
 *   (Form wie in ACL-Shop-Tools/docs/schnittstelle-merkliste.md; `color` haelt die
 *   gemerkte Farbe, solange noch keine Groesse gewaehlt ist, also variantId null.)
 * - Jede Aenderung feuert `wishlist:changed` am document, detail.items = ganze Liste.
 * - Lesezeichen-Knoepfe mit [data-wishlist-button] (snippets/wishlist-button.liquid),
 *   Ereignis-Delegation am document, Zustand ueber aria-pressed und .is-active;
 *   auf der Produktseite quadratisch, Kantenlaenge = Hoehe des Warenkorb-Knopfs.
 * - Zaehler: [data-wishlist-count] im Header (snippets/wishlist-icon.liquid).
 * - Produktseite: haelt data-variant-id ueber `variant:change` (Impulse) und
 *   `variant:changed` (Lauf-/Lifestyle-PDP) aktuell.
 * - Klaviyo: Ereignisse nur, wenn window.klaviyo vorhanden und der Browser
 *   identifiziert ist; die Anmeldung zu Erinnerung und Newsletter (jedes
 *   Speichern, Hinweis ueber dem Knopf) macht Modul 4 ueber Klaviyos Client-API.
 * - Oeffentlich: theme.wishlist (items, has, add, remove, restore, update, replace, sync).
 * - Stufe 2 (Modul unten): theme.WishlistSync – gespeicherte Liste ueber den App Proxy.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};
  if (theme.wishlist) {
    return;
  }

  var STORAGE_KEY = 'acl_wishlist';
  var MAX_ITEMS = 100;

  function strings() {
    return (theme.strings && theme.strings.wishlist) || {};
  }

  function parseJSON(text, fallback) {
    if (!text) {
      return fallback;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  /*============================================================================
    Ablage
  ==============================================================================*/
  var memory = null; // Ersatz, wenn localStorage nicht schreibbar ist (privater Modus)

  function isValid(entry) {
    return !!entry &&
      typeof entry.handle === 'string' &&
      entry.handle.length > 0 &&
      entry.handle.length <= 255 &&
      typeof entry.productId === 'number';
  }

  function read() {
    if (memory !== null) {
      return memory.slice();
    }
    var list;
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      list = raw ? parseJSON(raw, []) : [];
    } catch (e) {
      memory = [];
      return [];
    }
    if (!Array.isArray(list)) {
      list = [];
    }
    return list.filter(isValid);
  }

  function write(list) {
    if (memory !== null) {
      memory = list.slice();
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      memory = list.slice();
    }
  }

  function normalize(entry) {
    return {
      handle: String(entry.handle || ''),
      productId: toInt(entry.productId),
      variantId: entry.variantId == null ? null : toInt(entry.variantId),
      addedAt: entry.addedAt || new Date().toISOString(),
      price: entry.price == null ? null : toInt(entry.price),
      color: entry.color || null
    };
  }

  function emit(action, item, list) {
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { items: list, action: action, item: item || null }
    }));
  }

  var wishlist = {
    maxItems: MAX_ITEMS,

    items: read,

    count: function () {
      return read().length;
    },

    get: function (handle) {
      return read().filter(function (item) { return item.handle === handle; })[0] || null;
    },

    has: function (handle) {
      return !!wishlist.get(handle);
    },

    // Rueckgabe: { ok: true, item } oder { ok: false, reason: 'invalid' | 'exists' | 'full' }
    add: function (entry) {
      var item = normalize(entry || {});
      if (!isValid(item)) {
        return { ok: false, reason: 'invalid' };
      }
      var list = read();
      if (list.some(function (i) { return i.handle === item.handle; })) {
        return { ok: false, reason: 'exists', item: item };
      }
      if (list.length >= MAX_ITEMS) {
        return { ok: false, reason: 'full' };
      }
      list.push(item);
      write(list);
      emit('add', item, list);
      return { ok: true, item: item };
    },

    // Rueckgabe: { item, index } fuer „Rueckgaengig", sonst null
    remove: function (handle) {
      var list = read();
      var index = -1;
      list.forEach(function (item, n) {
        if (item.handle === handle) {
          index = n;
        }
      });
      if (index < 0) {
        return null;
      }
      var removed = list.splice(index, 1)[0];
      write(list);
      emit('remove', removed, list);
      return { item: removed, index: index };
    },

    // Entfernten Eintrag an alter Stelle wieder einsetzen
    restore: function (item, index) {
      var list = read();
      if (!isValid(item) || list.some(function (i) { return i.handle === item.handle; })) {
        return false;
      }
      if (list.length >= MAX_ITEMS) {
        return false;
      }
      var at = typeof index === 'number' ? Math.max(0, Math.min(index, list.length)) : list.length;
      list.splice(at, 0, item);
      write(list);
      emit('restore', item, list);
      return true;
    },

    // Felder eines Eintrags aendern, z. B. variantId nach der Groessenwahl
    update: function (handle, patch) {
      var list = read();
      var found = null;
      list.forEach(function (item) {
        if (item.handle === handle) {
          found = item;
          Object.keys(patch || {}).forEach(function (key) {
            item[key] = patch[key];
          });
        }
      });
      if (!found) {
        return null;
      }
      write(list);
      emit('update', found, list);
      return found;
    },

    // Ganze Liste ersetzen (Stufe 2: Vereinigung mit der Serverliste). Hoechstens
    // MAX_ITEMS; bei Ueberlauf fallen die aeltesten Eintraege weg.
    replace: function (entries) {
      var list = [];
      var seen = Object.create(null); // ohne Prototyp: ein Handle „constructor" darf nichts erben
      (Array.isArray(entries) ? entries : []).forEach(function (entry) {
        var item = normalize(entry || {});
        if (!isValid(item) || seen[item.handle]) {
          return;
        }
        seen[item.handle] = true;
        list.push(item);
      });
      if (list.length > MAX_ITEMS) {
        var keep = list.slice().sort(function (a, b) {
          return String(b.addedAt).localeCompare(String(a.addedAt));
        }).slice(0, MAX_ITEMS);
        list = list.filter(function (item) {
          return keep.indexOf(item) > -1;
        });
      }
      write(list);
      emit('replace', null, list);
      return list;
    },

    sync: function () {
      syncAll();
    }
  };

  theme.wishlist = wishlist;

  // Aenderungen aus anderen Tabs desselben Browsers uebernehmen
  window.addEventListener('storage', function (evt) {
    if (evt.key === STORAGE_KEY) {
      emit('sync', null, read());
    }
  });

  /*============================================================================
    Klaviyo – nur bei identifiziertem Browser und nur, wenn der Kunde beim
    Speichern die Erinnerung bekommen hat (Flag setzt theme.WishlistSync).
    Blosses Merken bleibt im Browser, da geht nichts raus (03.11.2026).
  ==============================================================================*/
  function reminderActive() {
    var sync = theme.WishlistSync;
    return !!(sync && typeof sync.reminderActive === 'function' && sync.reminderActive());
  }

  function klaviyoTrack(name, props) {
    var k = window.klaviyo;
    if (!k || typeof k.isIdentified !== 'function' || typeof k.track !== 'function' || !reminderActive()) {
      return;
    }
    try {
      var result = k.isIdentified();
      if (result && typeof result.then === 'function') {
        result.then(function (identified) {
          if (identified) {
            k.track(name, props);
          }
        }).catch(function () {});
      } else if (result === true) {
        k.track(name, props);
      }
    } catch (e) {
      // Klaviyo ist Beiwerk, die Liste funktioniert ohne
    }
  }

  document.addEventListener('wishlist:changed', function (evt) {
    var detail = evt.detail || {};
    if (detail.action === 'sync' || !detail.items) {
      return;
    }
    klaviyoTrack('Wishlist Updated', {
      Count: detail.items.length,
      Items: detail.items.map(function (item) {
        return {
          ProductID: item.productId,
          Handle: item.handle,
          VariantID: item.variantId,
          Color: item.color,
          Price: item.price == null ? null : item.price / 100,
          AddedAt: item.addedAt
        };
      })
    });
  });

  /*============================================================================
    Hinweis (z. B. „Liste voll")
  ==============================================================================*/
  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!message) {
      return;
    }
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3200);
  }

  wishlist.toast = toast;

  /*============================================================================
    Lesezeichen-Knoepfe
  ==============================================================================*/

  // Farbe, die die Karte gerade zeigt: aktives Farbfeld bzw. aktives Farbbild
  function activeCardColor(btn) {
    var card = btn.closest('.grid-product');
    if (!card) {
      return null;
    }
    var swatch = card.querySelector('.color-swatch.is-active');
    if (!swatch) {
      var image = card.querySelector('.grid-product__color-image.is-active');
      var match = image && image.className.match(/grid-product__color-image--(\d+)/);
      if (match) {
        swatch = card.querySelector('.color-swatch[data-variant-id="' + match[1] + '"]');
      }
    }
    if (!swatch) {
      return null;
    }
    var label = swatch.querySelector('.visually-hidden');
    var name = label ? label.textContent.trim() : '';
    if (!name) {
      name = (swatch.getAttribute('aria-label') || '').split(' - ').pop().trim();
    }
    return name || null;
  }

  function entryFromButton(btn) {
    var data = btn.dataset;
    var entry = {
      handle: data.handle,
      productId: toInt(data.productId),
      variantId: toInt(data.variantId),
      addedAt: new Date().toISOString(),
      price: toInt(data.price),
      color: data.color || null
    };

    if (data.wishlistContext === 'card') {
      var map = parseJSON(data.colorVariants, null);
      var color = activeCardColor(btn) || entry.color;
      if (map && color && Object.prototype.hasOwnProperty.call(map, color)) {
        var info = map[color] || [];
        entry.color = color;
        entry.variantId = info[0] == null ? null : toInt(info[0]);
        if (info[1] != null) {
          entry.price = toInt(info[1]);
        }
      }
    }

    return entry;
  }

  function bounce(btn) {
    btn.classList.remove('is-bouncing');
    void btn.offsetWidth; // Animation neu starten
    btn.classList.add('is-bouncing');
  }

  function toggleFromButton(btn) {
    var handle = btn.dataset.handle;
    if (!handle) {
      return;
    }

    if (wishlist.has(handle)) {
      wishlist.remove(handle);
      return;
    }

    var entry = entryFromButton(btn);
    var result = wishlist.add(entry);
    if (!result.ok) {
      if (result.reason === 'full') {
        toast(strings().full || 'Your wishlist is full.');
      }
      return;
    }

    bounce(btn);

    klaviyoTrack('Added to Wishlist', {
      ProductName: btn.dataset.title || '',
      ProductID: entry.productId,
      Handle: handle,
      VariantID: entry.variantId,
      Color: entry.color,
      ImageURL: btn.dataset.image || '',
      URL: btn.dataset.url || '',
      Price: entry.price == null ? null : entry.price / 100
    });
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('[data-wishlist-button]') : null;
    if (!btn) {
      return;
    }
    // Auf der Karte darf der Klick nicht den Produktlink ausloesen
    evt.preventDefault();
    evt.stopPropagation();
    toggleFromButton(btn);
  });

  document.addEventListener('animationend', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('.wishlist-button') : null;
    if (btn) {
      btn.classList.remove('is-bouncing');
    }
  });

  function syncButtons(scope) {
    var listed = Object.create(null); // ohne Prototyp, s. replace()
    read().forEach(function (item) {
      listed[item.handle] = true;
    });
    (scope || document).querySelectorAll('[data-wishlist-button]').forEach(function (btn) {
      var active = !!listed[btn.dataset.handle];
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? btn.dataset.labelRemove : btn.dataset.labelAdd;
      if (label) {
        btn.setAttribute('aria-label', label);
      }
    });
  }

  /*============================================================================
    Zaehler im Header
  ==============================================================================*/
  function syncCount() {
    var count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      el.classList.toggle('wishlist-link__bubble--visible', count > 0);
      // Lesezeichen im Header gefuellt, sobald etwas auf der Liste ist
      var link = el.closest('.wishlist-link');
      if (link) {
        link.classList.toggle('is-active', count > 0);
      }
    });
  }

  /*============================================================================
    Produktseite: Knopf quadratisch, genau so hoch wie der Warenkorb-Knopf
    daneben. Flexbox streckt den Knopf zwar auf Zeilenhoehe, leitet daraus
    aber keine Breite ab – deshalb hier gemessen, auch bei jeder Groessen-
    aenderung des Warenkorb-Knopfs (Umbruch, Schriftladen, Textwechsel).
    Gemessen wird, waehrend der Knopf kurz aus dem Fluss ist: sonst nimmt er
    dem Warenkorb-Knopf Breite weg, dessen Text bricht um, er wird hoeher,
    der Knopf groesser – eine Rueckkopplung ohne Ende.
  ==============================================================================*/
  var sizeObserver = window.ResizeObserver ? new ResizeObserver(function () {
    squareButtons();
  }) : null;

  function squareButtons() {
    document.querySelectorAll('.wishlist-button--product').forEach(function (btn) {
      var row = btn.parentNode;
      if (!row) {
        return;
      }
      var targets = row.querySelectorAll('[data-add-to-cart], [data-rp-atc], [data-rp-notify]');
      if (!targets.length) {
        return;
      }
      var shown = btn.style.display;
      btn.style.display = 'none';
      var height = 0;
      targets.forEach(function (atc) {
        if (sizeObserver) {
          sizeObserver.observe(atc);
        }
        if (!height) {
          height = Math.round(atc.getBoundingClientRect().height); // 0 = ausgeblendet
        }
      });
      btn.style.display = shown;
      if (height > 0) {
        btn.style.width = height + 'px';
        btn.style.height = height + 'px';
      }
    });
  }

  function syncAll() {
    syncButtons();
    syncCount();
    squareButtons();
  }

  document.addEventListener('wishlist:changed', syncAll);

  /*============================================================================
    Produktseite: gewaehlte Variante mitfuehren
  ==============================================================================*/
  function applyVariant(id, price) {
    if (id == null) {
      return;
    }
    var key = String(id);
    document.querySelectorAll('[data-wishlist-button][data-wishlist-context="product"]').forEach(function (btn) {
      var map = parseJSON(btn.dataset.variants, null);
      if (!map || !Object.prototype.hasOwnProperty.call(map, key)) {
        return; // Variante gehoert zu einem anderen Produkt auf der Seite
      }
      var info = map[key] || [];
      btn.dataset.variantId = key;
      var newPrice = price != null ? price : info[0];
      if (newPrice != null) {
        btn.dataset.price = String(newPrice);
      }
      if (info[1] != null) {
        btn.dataset.color = info[1];
      }
    });
  }

  document.addEventListener('variant:change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    if (variant) {
      applyVariant(variant.id, variant.price);
    }
  });

  document.addEventListener('variant:changed', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    if (variant) {
      applyVariant(variant.id, null);
    }
  });

  /*============================================================================
    Start – auch fuer Herzen, die spaeter per JavaScript in die Seite kommen
    (Empfehlungen, zuletzt angesehen, Suche)
  ==============================================================================*/
  var syncTimer = null;

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAll, 30);
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) {
          continue;
        }
        if (node.matches('[data-wishlist-button], [data-wishlist-count]') ||
            node.querySelector('[data-wishlist-button], [data-wishlist-count]')) {
          scheduleSync();
          return;
        }
      }
    }
  });

  function init() {
    syncAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*============================================================================
  Wunschlisten-Seite – sections/wishlist.liquid
  Holt je Eintrag /products/<handle>.js (parallel, fuer die Sitzung
  zwischengespeichert) und baut daraus Kacheln mit Farbfeldern, Groessen-Auswahl,
  „In den Warenkorb" (einzeln und alle; was im Warenkorb landet,
  faellt von der Liste) und Entfernen mit „Rueckgaengig".
==============================================================================*/
(function () {
  'use strict';

  window.theme = window.theme || {};
  var wishlist = theme.wishlist;
  if (!wishlist || theme.WishlistSection) {
    return;
  }

  var CACHE_KEY = 'acl_wishlist_products';
  var CACHE_TTL = 10 * 60 * 1000;
  var UNDO_MS = 5000;
  var IMAGE_WIDTHS = [360, 540, 720, 900];
  var COLOR_WORDS = ['colour', 'color', 'farbe', 'couleur', 'kleur', 'colore', 'seasonal', 'core'];
  var SIZE_WORDS = ['size', 'größe', 'groesse', 'taille', 'maat', 'talla', 'taglia', 'tamanho'];

  function strings() {
    return (theme.strings && theme.strings.wishlist) || {};
  }

  function t(key, fallback) {
    var s = strings();
    return s[key] != null ? s[key] : (fallback || '');
  }

  function plural(key, count) {
    var s = strings();
    var text = count === 1 ? s[key + 'One'] : s[key + 'Other'];
    return (text || '[count]').replace('[count]', count);
  }

  function parseJSON(text, fallback) {
    if (!text) {
      return fallback;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(cents) {
    try {
      if (theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (e) {
      // unten der schlichte Ersatz
    }
    return (cents / 100).toFixed(2);
  }

  // Immer absolut mit Protokoll: Klaviyo bekommt die Adresse (Mail-Vorlage),
  // dort nuetzt „//cdn…" oder ein relativer Pfad nichts
  function imageUrl(src, width) {
    if (!src) {
      return '';
    }
    try {
      src = new URL(src, window.location.href).href;
    } catch (e) {
      if (src.indexOf('//') === 0) {
        src = 'https:' + src;
      }
    }
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function rootUrl() {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root;
    return root || '/';
  }

  function nameMatches(name, trigger, words) {
    var lower = (name || '').toLowerCase();
    if (trigger && lower.indexOf(String(trigger).toLowerCase()) > -1) {
      return true;
    }
    return words.some(function (word) {
      return lower.indexOf(word) > -1;
    });
  }

  function isColorOption(name) {
    return nameMatches(name, strings().colorTrigger, COLOR_WORDS);
  }

  function isSizeOption(name) {
    return nameMatches(name, strings().sizeTrigger, SIZE_WORDS);
  }

  function unique(list) {
    var seen = [];
    list.forEach(function (value) {
      if (value != null && seen.indexOf(value) < 0) {
        seen.push(value);
      }
    });
    return seen;
  }

  /*---------- Produktdaten ----------*/
  function cacheRead() {
    try {
      return parseJSON(window.sessionStorage.getItem(CACHE_KEY), {}) || {};
    } catch (e) {
      return {};
    }
  }

  function cacheGet(handle) {
    var hit = cacheRead()[handle];
    return hit && (Date.now() - hit.t) < CACHE_TTL ? hit.p : null;
  }

  function cacheSet(handle, product) {
    try {
      var all = cacheRead();
      all[handle] = { t: Date.now(), p: product };
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
    } catch (e) {
      // ohne Zwischenspeicher geht es auch
    }
  }

  function slimImage(image) {
    if (!image || !image.src) {
      return null;
    }
    return { src: image.src, width: image.width || 0, height: image.height || 0 };
  }

  // Nur, was die Kachel braucht – haelt den Zwischenspeicher klein
  function slim(product) {
    var variants = (product.variants || []).map(function (v) {
      return {
        id: v.id,
        options: v.options || [v.option1, v.option2, v.option3].filter(function (o) { return o != null; }),
        available: !!v.available,
        price: v.price,
        compare_at_price: v.compare_at_price,
        image: slimImage(v.featured_image)
      };
    });
    var options = (product.options || []).map(function (option, index) {
      if (typeof option === 'string') {
        return { name: option, values: unique(variants.map(function (v) { return v.options[index]; })) };
      }
      var values = option.values && option.values.length ? option.values : unique(variants.map(function (v) { return v.options[index]; }));
      return { name: option.name, values: values };
    });
    var media = (product.media || []).filter(function (m) {
      return m.media_type === 'image' && m.preview_image;
    })[0];
    var image = media ? slimImage(media.preview_image) : null;
    if (!image && product.featured_image) {
      image = { src: product.featured_image, width: 0, height: 0 };
    }
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      available: !!product.available,
      image: image,
      options: options,
      variants: variants
    };
  }

  function fetchProduct(handle) {
    var cached = cacheGet(handle);
    if (cached) {
      return Promise.resolve(cached);
    }
    return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (product) {
        var slimmed = slim(product);
        cacheSet(handle, slimmed);
        return slimmed;
      });
  }

  /*---------- Warenkorb ----------*/
  function cartAdd(items) {
    var url = (theme.routes && theme.routes.cartAdd) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        return { ok: response.ok, status: response.status, data: data };
      });
    });
  }

  function errorText(data) {
    if (data && typeof data.description === 'string' && data.description) {
      return data.description;
    }
    if (data && typeof data.message === 'string' && data.message && data.message !== 'Cart Error') {
      return data.message;
    }
    return t('cartError', 'Could not be added to your cart.');
  }

  /*---------- Kachel ----------*/
  function Tile(section, entry, product) {
    this.section = section;
    this.entry = entry;
    this.product = product || null;
    this.selection = [];
    this.colorIndex = -1;
    this.optionValues = [];
    this.resolved = null;
    this.removed = null;
    this.incomplete = false;
    this.busy = false;
    this.currentImage = null;
    this.el = section.template.content.firstElementChild.cloneNode(true);
    this.build();
  }

  Tile.prototype = {
    q: function (name) {
      return this.el.querySelector('[data-tile-' + name + ']');
    },

    build: function () {
      var self = this;
      var el = this.el;
      var product = this.product;

      el.dataset.handle = this.entry.handle;

      el.addEventListener('click', function (evt) {
        var choice = evt.target.closest('[data-option-index]');
        if (choice && el.contains(choice)) {
          evt.preventDefault();
          self.select(parseInt(choice.dataset.optionIndex, 10), choice.dataset.value);
          return;
        }
        if (evt.target.closest('[data-tile-add]')) {
          self.addToCart();
        } else if (evt.target.closest('[data-tile-remove]')) {
          self.remove();
        } else if (evt.target.closest('[data-tile-undo-btn]')) {
          self.undo();
        }
      });

      el.addEventListener('change', function (evt) {
        var select = evt.target.closest('[data-option-select]');
        if (select && el.contains(select)) {
          self.select(parseInt(select.dataset.optionSelect, 10), select.value || null);
        }
      });

      if (!product) {
        // Produkt nicht (mehr) erreichbar: nur Hinweis und Entfernen
        this.q('title').textContent = this.entry.handle.replace(/-/g, ' ');
        this.q('link').removeAttribute('href');
        this.q('unavailable').hidden = false;
        this.q('add').hidden = true;
        return;
      }

      this.q('link').href = rootUrl() + 'products/' + product.handle;
      this.q('title').textContent = product.title;

      product.options.forEach(function (option, index) {
        if (self.colorIndex < 0 && isColorOption(option.name)) {
          self.colorIndex = index;
        }
      });
      this.optionValues = product.options.map(function (option) {
        return option.values.slice();
      });

      // Vorbelegung: gespeicherte Variante, sonst gespeicherte oder erste Farbe,
      // dazu alles, was ohnehin nur einen Wert hat
      this.selection = product.options.map(function () { return null; });
      var saved = this.entry.variantId != null ? product.variants.filter(function (v) {
        return v.id === self.entry.variantId;
      })[0] : null;

      if (saved) {
        this.selection = saved.options.slice();
      } else {
        if (this.colorIndex > -1 && this.entry.color && this.optionValues[this.colorIndex].indexOf(this.entry.color) > -1) {
          this.selection[this.colorIndex] = this.entry.color;
        }
        this.optionValues.forEach(function (values, index) {
          if (values.length === 1) {
            self.selection[index] = values[0];
          }
        });
        if (this.colorIndex > -1 && this.selection[this.colorIndex] == null) {
          this.selection[this.colorIndex] = this.optionValues[this.colorIndex][0] || null;
        }
      }

      this.update();
      this.persist();
    },

    // Varianten, die zur (teilweisen) Auswahl passen
    candidates: function () {
      var selection = this.selection;
      return this.product.variants.filter(function (variant) {
        return selection.every(function (value, index) {
          return value == null || variant.options[index] === value;
        });
      });
    },

    resolve: function () {
      if (!this.product || this.selection.some(function (value) { return value == null; })) {
        return null;
      }
      return this.candidates()[0] || null;
    },

    // Werte einer Option, die es in der aktiven Farbe gibt (Reihenfolge wie im Produkt)
    valuesFor: function (index) {
      var colorIndex = this.colorIndex;
      var color = colorIndex > -1 ? this.selection[colorIndex] : null;
      var values = unique(this.product.variants.filter(function (variant) {
        return color == null || variant.options[colorIndex] === color;
      }).map(function (variant) {
        return variant.options[index];
      }));
      var order = this.optionValues[index];
      values.sort(function (a, b) { return order.indexOf(a) - order.indexOf(b); });
      return values;
    },

    // Gibt es diesen Wert in der aktiven Farbe lieferbar?
    isAvailable: function (index, value) {
      var colorIndex = this.colorIndex;
      var color = colorIndex > -1 ? this.selection[colorIndex] : null;
      return this.product.variants.some(function (variant) {
        if (color != null && index !== colorIndex && variant.options[colorIndex] !== color) {
          return false;
        }
        return variant.options[index] === value && variant.available;
      });
    },

    // Farbfeld wie snippets/lifestyle-swatch-style.liquid seit dem Live-Stand vom
    // 26.10.2026: Farbmuster <farbname>.png aus den Shop-Dateien, sonst der Name
    // als CSS-Farbe (chocolate, yellow, black …), sonst neutrales Grau.
    // Metafeld-Muster (value.swatch, running_color_content) kennt JavaScript nicht.
    styleSwatch: function (button, value) {
      var name = String(value || '').toLowerCase();
      var cssColor = name
        .replace('off white', '#f4f1e8').replace('off-white', '#f4f1e8')
        .replace('schwarz', 'black').replace('weiß', 'white').replace('weiss', 'white')
        .replace('grau', 'gray').replace('navy', '#202b40')
        .replace(/\s+/g, '');
      button.style.backgroundColor = '#f2f1ee';
      button.style.backgroundColor = cssColor; // ungueltige Namen laesst der Browser fallen
      var base = this.filesBase();
      if (base) {
        var handle = name.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        if (handle) {
          button.style.backgroundImage = 'url(' + base + handle + '.png?width=120)';
        }
      }
    },

    // Basis der Shop-Dateien (…/s/files/…/files/) aus einer Produktbild-URL ableiten
    filesBase: function () {
      var src = (this.product.image && this.product.image.src) || '';
      var match = src.match(/^(.*\/s\/files\/[0-9\/]+\/)(?:files|products)\//);
      return match ? match[1] + 'files/' : null;
    },

    colorImage: function (color) {
      if (this.colorIndex < 0 || color == null) {
        return null;
      }
      var colorIndex = this.colorIndex;
      var variant = this.product.variants.filter(function (v) {
        return v.options[colorIndex] === color && v.image;
      })[0];
      return variant ? variant.image : null;
    },

    select: function (index, value) {
      var self = this;
      if (index < 0 || index >= this.selection.length) {
        return;
      }
      this.selection[index] = value;
      if (index === this.colorIndex) {
        // Groesse behalten, wenn es sie in der neuen Farbe gibt, sonst leeren
        this.selection.forEach(function (chosen, i) {
          if (i !== index && chosen != null && self.valuesFor(i).indexOf(chosen) < 0) {
            self.selection[i] = null;
          }
        });
      }
      this.incomplete = false;
      this.el.classList.remove('is-incomplete');
      this.q('hint').hidden = true;
      this.q('error').hidden = true;
      this.update();
      this.persist();
    },

    // Auswahl in die Ablage schreiben, damit die Liste sie sich merkt
    persist: function () {
      var color = this.colorIndex > -1 ? (this.selection[this.colorIndex] || null) : null;
      var variantId = this.resolved ? this.resolved.id : null;
      if (this.entry.variantId === variantId && (this.entry.color || null) === color) {
        return;
      }
      this.entry.variantId = variantId;
      this.entry.color = color;
      this.section.muted = true;
      wishlist.update(this.entry.handle, { variantId: variantId, color: color });
      this.section.muted = false;
    },

    update: function () {
      var self = this;
      var product = this.product;
      this.resolved = this.resolve();

      // Farbfelder (nur bei mehr als einer Farbe)
      var swatches = this.q('swatches');
      var color = this.colorIndex > -1 ? this.selection[this.colorIndex] : null;
      if (this.colorIndex > -1 && this.optionValues[this.colorIndex].length > 1) {
        swatches.innerHTML = '';
        this.optionValues[this.colorIndex].forEach(function (value) {
          var button = document.createElement('button');
          var active = value === color;
          button.type = 'button';
          button.className = 'color-swatch color-swatch--small wishlist-tile__swatch' + (active ? ' is-active' : '');
          button.dataset.optionIndex = String(self.colorIndex);
          button.dataset.value = value;
          button.setAttribute('aria-label', value);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
          self.styleSwatch(button, value);
          var label = document.createElement('span');
          label.className = 'visually-hidden';
          label.textContent = value;
          button.appendChild(label);
          swatches.appendChild(button);
        });
        swatches.hidden = false;
      } else {
        swatches.hidden = true;
      }

      var colorName = this.q('color-name');
      colorName.textContent = color || '';
      colorName.hidden = !color;

      // Auswahlfeld fuer jede andere Option (Daniel, 01.11.2026: Select statt
      // Pillen), nur die Werte der aktiven Farbe. Die Felder bleiben stehen und
      // werden nur neu befuellt, damit der Fokus nach der Wahl nicht verloren geht
      var selects = this.q('selects');
      var anySelect = false;
      this.selectEls = this.selectEls || {};
      product.options.forEach(function (option, index) {
        var select = self.selectEls[index];
        if (index === self.colorIndex || self.optionValues[index].length <= 1) {
          return;
        }
        if (!select) {
          select = document.createElement('select');
          select.className = 'wishlist-tile__select';
          select.dataset.optionSelect = String(index);
          select.setAttribute('aria-label', option.name);
          selects.appendChild(select);
          self.selectEls[index] = select;
        }
        var chosen = self.selection[index];
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.textContent = isSizeOption(option.name) ? t('selectSize', 'Select size') : option.name;
        select.innerHTML = '';
        select.appendChild(placeholder);
        self.valuesFor(index).forEach(function (value) {
          var item = document.createElement('option');
          item.value = value;
          item.textContent = self.isAvailable(index, value) ? value : value + ' – ' + t('soldOut', 'Sold out');
          select.appendChild(item);
        });
        select.value = chosen == null ? '' : chosen;
        select.classList.toggle('is-empty', chosen == null);
        anySelect = true;
      });
      selects.hidden = !anySelect;

      // Bild: Variante, sonst Farbe, sonst Produkt
      this.setImage((this.resolved && this.resolved.image) || this.colorImage(color) || product.image);

      // Preis der gewaehlten Variante, sonst „ab" dem guenstigsten Kandidaten
      var price = null;
      var compare = null;
      var from = false;
      if (this.resolved) {
        price = this.resolved.price;
        compare = this.resolved.compare_at_price;
      } else {
        var prices = this.candidates().map(function (v) { return v.price; });
        if (prices.length) {
          price = Math.min.apply(null, prices);
          from = Math.max.apply(null, prices) !== price;
        }
      }
      var priceEl = this.q('price');
      if (price == null) {
        priceEl.innerHTML = '';
      } else {
        var html = '<div>' + (from ? escapeHtml(theme.strings && theme.strings.productFrom || '') : '') + formatMoney(price) + '</div>';
        if (compare && compare > price) {
          html += '<span class="grid-product__price--original">' + formatMoney(compare) + '</span>';
        }
        priceEl.innerHTML = html;
      }
      this.q('price-drop').hidden = !(this.entry.price != null && price != null && this.entry.price > price);

      // Knopf: aktiv nur bei vollstaendiger Wahl; ausverkauft = nicht klickbar
      var add = this.q('add');
      if (this.resolved && !this.resolved.available) {
        add.disabled = true;
        add.removeAttribute('aria-disabled');
        add.textContent = t('soldOut', 'Sold out');
      } else {
        add.disabled = false;
        add.textContent = t('addToCart', 'Add to cart');
        if (this.resolved) {
          add.removeAttribute('aria-disabled');
        } else {
          add.setAttribute('aria-disabled', 'true');
        }
      }
    },

    setImage: function (image) {
      var img = this.q('image');
      var wrap = this.q('image-wrap');
      if (!image || !image.src) {
        return;
      }
      if (image.width && image.height) {
        wrap.style.paddingBottom = (image.height / image.width * 100).toFixed(2) + '%';
      }
      if (this.currentImage === image.src) {
        return;
      }
      this.currentImage = image.src;
      img.classList.remove('is-loaded');
      img.onload = function () {
        img.classList.add('is-loaded');
      };
      img.alt = this.product.title;
      img.srcset = IMAGE_WIDTHS.map(function (width) {
        return imageUrl(image.src, width) + ' ' + width + 'w';
      }).join(', ');
      img.src = imageUrl(image.src, 540);
      if (img.complete && img.naturalWidth) {
        img.classList.add('is-loaded');
      }
    },

    // „Bitte Groesse waehlen" an der ersten fehlenden Option
    markIncomplete: function () {
      if (!this.product) {
        return;
      }
      var missing = -1;
      this.selection.forEach(function (value, index) {
        if (missing < 0 && value == null) {
          missing = index;
        }
      });
      if (missing < 0) {
        return;
      }
      var name = this.product.options[missing].name;
      var hint = this.q('hint');
      hint.textContent = isSizeOption(name) ? t('chooseSize', 'Please choose a size') : t('chooseOption', 'Please choose [option]').replace('[option]', function () { return name; });
      hint.hidden = false;
      this.incomplete = true;
      this.el.classList.add('is-incomplete');
    },

    showError: function (message) {
      var error = this.q('error');
      error.textContent = message;
      error.hidden = false;
    },

    // Liegt im Warenkorb: runter von der Liste (Davids Wunsch, 04.11.2026), ohne
    // „Rueckgaengig". section.muted, sonst baut die Seite alle Kacheln neu auf und
    // die uebrigen verlieren ihre Auswahl. Waehrend des Aufrufs schon entfernt:
    // dort steht „Rueckgaengig", das bleibt stehen.
    leaveForCart: function () {
      if (this.removed) {
        return;
      }
      this.section.muted = true;
      wishlist.remove(this.entry.handle);
      this.section.muted = false;
      this.destroy();
    },

    addToCart: function () {
      var self = this;
      var add = this.q('add');
      if (!this.product || this.removed || this.busy) {
        return;
      }
      if (!this.resolved) {
        this.markIncomplete();
        return;
      }
      if (!this.resolved.available) {
        return;
      }
      this.busy = true;
      this.q('error').hidden = true;
      add.classList.add('btn--loading');
      cartAdd([{ id: this.resolved.id, quantity: 1 }]).then(function (result) {
        self.busy = false;
        add.classList.remove('btn--loading');
        if (!result.ok) {
          self.showError(errorText(result.data));
          return;
        }
        self.leaveForCart();
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      }).catch(function () {
        self.busy = false;
        add.classList.remove('btn--loading');
        self.showError(t('cartError', 'Could not be added to your cart.'));
      });
    },

    // Entfernen ohne Rueckfrage, fuenf Sekunden „Rueckgaengig"
    remove: function () {
      var self = this;
      if (this.removed) {
        return;
      }
      this.section.muted = true;
      var result = wishlist.remove(this.entry.handle);
      this.section.muted = false;
      this.removed = result || { item: this.entry, index: 0 };
      this.el.classList.add('is-removed');
      this.q('undo').hidden = false;
      this.undoTimer = setTimeout(function () {
        self.destroy();
      }, UNDO_MS);
      this.section.refresh();
    },

    undo: function () {
      if (!this.removed) {
        return;
      }
      clearTimeout(this.undoTimer);
      this.section.muted = true;
      var restored = wishlist.restore(this.removed.item, this.removed.index);
      this.section.muted = false;
      this.removed = null;
      this.el.classList.remove('is-removed');
      this.q('undo').hidden = true;
      if (!restored) {
        this.destroy();
        return;
      }
      this.section.refresh();
    },

    destroy: function () {
      clearTimeout(this.undoTimer);
      if (this.el.parentNode) {
        this.el.parentNode.removeChild(this.el);
      }
      this.section.tiles = this.section.tiles.filter(function (tile) { return tile !== this; }, this);
      this.section.refresh();
    }
  };

  /*---------- Section ----------*/
  theme.WishlistSection = function (container) {
    this.container = container;
    this.grid = container.querySelector('[data-wishlist-grid]');
    this.template = container.querySelector('[data-wishlist-tile-template]');
    this.toolbar = container.querySelector('[data-wishlist-toolbar]');
    this.countLabel = container.querySelector('[data-wishlist-count-label]');
    this.addAllButton = container.querySelector('[data-wishlist-add-all]');
    this.empty = container.querySelector('[data-wishlist-empty]');
    this.loading = container.querySelector('[data-wishlist-loading]');
    this.tiles = [];
    this.muted = false;
    this.busy = false;
    this.loadId = 0;

    if (!this.grid || !this.template) {
      return;
    }

    this.onChanged = this.onChanged.bind(this);
    document.addEventListener('wishlist:changed', this.onChanged);
    if (this.addAllButton) {
      this.addAllButton.addEventListener('click', this.addAll.bind(this));
    }
    this.load();
  };

  theme.WishlistSection.prototype = {
    onUnload: function () {
      document.removeEventListener('wishlist:changed', this.onChanged);
      this.tiles.forEach(function (tile) { clearTimeout(tile.undoTimer); });
    },

    // Aenderungen von anderswo (anderer Tab, Herz an anderer Stelle): neu aufbauen
    onChanged: function (evt) {
      if (this.muted) {
        return;
      }
      var action = evt.detail && evt.detail.action;
      if (action === 'update') {
        return;
      }
      this.load();
    },

    load: function () {
      var self = this;
      var loadId = ++this.loadId;
      var items = wishlist.items().reverse(); // neueste zuerst

      this.tiles.forEach(function (tile) { clearTimeout(tile.undoTimer); });
      this.tiles = [];
      this.grid.innerHTML = '';

      if (!items.length) {
        this.loading.hidden = true;
        this.refresh();
        return;
      }

      this.loading.hidden = false;
      this.empty.hidden = true;
      this.addAllButton.hidden = true;

      Promise.all(items.map(function (entry) {
        return fetchProduct(entry.handle).then(function (product) {
          return { entry: entry, product: product };
        }, function () {
          return { entry: entry, product: null };
        });
      })).then(function (results) {
        if (loadId !== self.loadId) {
          return; // inzwischen neu geladen
        }
        self.loading.hidden = true;
        var fragment = document.createDocumentFragment();
        results.forEach(function (result) {
          var tile = new Tile(self, result.entry, result.product);
          self.tiles.push(tile);
          fragment.appendChild(tile.el);
        });
        self.grid.appendChild(fragment);
        self.refresh();
      });
    },

    // Die Leiste bleibt stehen (links das Speichern, Stufe 2); nur „Alle in den
    // Warenkorb" erscheint erst mit Kacheln
    refresh: function () {
      var hasTiles = this.tiles.length > 0;
      if (this.countLabel) {
        this.countLabel.textContent = plural('items', wishlist.count());
      }
      this.empty.hidden = hasTiles;
      this.addAllButton.hidden = !hasTiles;
      this.addAllButton.disabled = !this.tiles.some(function (tile) {
        return !tile.removed && tile.product;
      });
    },

    // Alle vollstaendigen Kacheln in einem Aufruf; zur ersten unvollstaendigen scrollen
    addAll: function () {
      var self = this;
      var active = this.tiles.filter(function (tile) { return !tile.removed && tile.product; });
      var complete = active.filter(function (tile) { return tile.resolved && tile.resolved.available; });
      var incomplete = active.filter(function (tile) { return !tile.resolved; });

      incomplete.forEach(function (tile) { tile.markIncomplete(); });
      if (incomplete.length) {
        incomplete[0].el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (!complete.length || this.busy) {
        return;
      }

      this.busy = true;
      this.addAllButton.classList.add('btn--loading');

      function finish(added) {
        self.busy = false;
        self.addAllButton.classList.remove('btn--loading');
        if (!added) {
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
        if (incomplete.length) {
          wishlist.toast(plural('addedCount', added));
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      }

      cartAdd(complete.map(function (tile) {
        return { id: tile.resolved.id, quantity: 1 };
      })).then(function (result) {
        if (result.ok) {
          complete.forEach(function (tile) { tile.leaveForCart(); });
          finish(complete.length);
          return;
        }
        // Shopify nimmt bei einem Fehler nichts an: einzeln nachlegen,
        // damit der Fehler an der betroffenen Kachel steht
        var added = 0;
        return complete.reduce(function (chain, tile) {
          return chain.then(function () {
            return cartAdd([{ id: tile.resolved.id, quantity: 1 }]).then(function (single) {
              if (single.ok) {
                added++;
                tile.leaveForCart();
              } else {
                tile.showError(errorText(single.data));
              }
            });
          });
        }, Promise.resolve()).then(function () {
          finish(added);
        });
      }).catch(function () {
        self.busy = false;
        self.addAllButton.classList.remove('btn--loading');
        wishlist.toast(t('cartError', 'Could not be added to your cart.'));
      });
    }
  };

  // Gemeinsame Helfer fuer die weiteren Module (Gespeicherte Liste, Erinnerung)
  wishlist.util = {
    fetchProduct: fetchProduct,
    formatMoney: formatMoney,
    imageUrl: imageUrl,
    rootUrl: rootUrl,
    isColorOption: isColorOption,
    isSizeOption: isSizeOption,
    unique: unique,
    errorText: errorText,
    t: t
  };

  function register() {
    if (theme.sections && typeof theme.sections.register === 'function') {
      theme.sections.register('wishlist', theme.WishlistSection);
    } else {
      document.querySelectorAll('[data-section-type="wishlist"]').forEach(function (container) {
        new theme.WishlistSection(container);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', register);
  } else {
    register();
  }
})();

/*============================================================================
  Gespeicherte Liste (Stufe 2) – Verbindung zu Shop-Tools
  Weg: Shopify App Proxy unter /apps/merkliste (Schnittstelle in
  ACL-Shop-Tools/docs/schnittstelle-merkliste.md). Die Liste lebt weiter im
  Browser; der Server haelt unter der E-Mail-Adresse eine Kopie. Verbunden
  ist ein Browser, sobald er einen Geraeteschluessel hat – aus dem Link in
  der Mail (?abruf=…) oder, bei angemeldeten Kunden, direkt ueber das Konto.
  Ein verbundener Browser schickt nach jeder Aenderung die ganze Liste
  (gebuendelt, 1 s nach der letzten Aenderung); antwortet der Server 401,
  ist die Verbindung weg, die Liste bleibt lokal.
  Das Formular „Liste speichern" liegt in sections/wishlist.liquid.
==============================================================================*/
(function () {
  'use strict';

  window.theme = window.theme || {};
  var wishlist = theme.wishlist;
  if (!wishlist || theme.WishlistSync) {
    return;
  }

  var BASE = '/apps/merkliste'; // immer ohne Sprachpraefix, Shopify reicht an Shop-Tools weiter
  var DEVICE_KEY = 'acl_wishlist_geraet';
  var EMAIL_KEY = 'acl_wishlist_verbunden';
  var ADDRESS_KEY = 'acl_wishlist_adresse'; // zuletzt im Formular benutzt, zum Vorbelegen
  var PUSH_DELAY = 1000;
  var LINK_RETRY_DELAY = 1000; // Netzfehler beim Abruf per Link: einmal von selbst wiederholen
  // Was der Server annimmt (Shop-Tools, docs/schnittstelle-merkliste.md): Kleinbuchstaben
  // auch jenseits von ASCII, Ziffern, Bindestrich, dazu ® und ™ – die SRC®-Linie und die
  // Tacx®-Flaschen tragen das ® im Handle (03.11.2026)
  // Als new RegExp, nicht als Literal: ein Literal mit u-Flag und \p{…} waere auf
  // alten Engines (vor Chrome 64, Safari 11.1, Firefox 78) ein Parse-Fehler der
  // ganzen Datei – dann waeren auch Herzen und Zaehler weg. So faellt nur die
  // Regel auf ASCII zurueck.
  var HANDLE_RE;
  try {
    HANDLE_RE = new RegExp('^[\\p{Ll}\\p{N}®™][\\p{Ll}\\p{N}®™-]*$', 'u');
  } catch (e) {
    HANDLE_RE = /^[a-z0-9®™][a-z0-9®™-]*$/;
  }
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function strings() {
    return (theme.strings && theme.strings.wishlist) || {};
  }

  function t(key, fallback) {
    var s = strings();
    return s[key] != null ? s[key] : (fallback || '');
  }

  // Element da, nicht hidden und gerendert (getClientRects leer = display:none)
  function visible(node) {
    if (!node || node.hidden) {
      return false;
    }
    return typeof node.getClientRects !== 'function' || node.getClientRects().length > 0;
  }

  function isEmail(value) {
    return EMAIL_RE.test(String(value || '').trim());
  }

  /*---------- Geraeteschluessel und Anzeige-Adresse ----------*/
  var memory = {};

  function getStored(key) {
    try {
      var value = window.localStorage.getItem(key);
      if (value != null) {
        return value;
      }
    } catch (e) {
      // privater Modus: unten der Ersatz
    }
    return memory[key] || null;
  }

  function setStored(key, value) {
    if (value == null) {
      delete memory[key];
    } else {
      memory[key] = value;
    }
    try {
      if (value == null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch (e) {
      // dann nur im Speicher
    }
  }

  function device() {
    return getStored(DEVICE_KEY);
  }

  function connected() {
    return !!device();
  }

  function email() {
    return getStored(EMAIL_KEY) || '';
  }

  // Vorschlag fuers E-Mail-Feld (Daniel, 01.11.2026): zuletzt hier benutzte
  // Adresse, sonst die, unter der Klaviyo den Browser kennt (Cookie __kla_id,
  // Base64-JSON; $email steht nur drin, wenn Klaviyo sie dort abgelegt hat)
  function suggestedAddress() {
    var stored = getStored(ADDRESS_KEY);
    if (isEmail(stored)) {
      return stored;
    }
    try {
      var match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      var data = match ? JSON.parse(window.atob(decodeURIComponent(match[1]))) : null;
      if (data && isEmail(data.$email)) {
        return String(data.$email).trim();
      }
    } catch (e) {
      // Cookie fehlt oder hat ein anderes Format
    }
    return '';
  }

  function locale() {
    var code = strings().locale || document.documentElement.lang || 'de';
    return String(code).slice(0, 5);
  }

  function emitConnection(isConnected, reason) {
    document.dispatchEvent(new CustomEvent('wishlist:connection', {
      detail: { connected: isConnected, email: email(), reason: reason || null }
    }));
  }

  /*---------- Aufrufe ----------*/
  function request(method, path, body, withDevice) {
    var headers = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (withDevice) {
      headers['X-Merkliste-Geraet'] = device() || '';
    }
    return fetch(BASE + path, {
      method: method,
      headers: headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          data = null;
        }
        return { ok: response.ok, status: response.status, data: data };
      });
    });
  }

  // Die Liste in der Form, die der Server prueft; Eintraege mit fremdem Handle bleiben nur lokal
  function exportList() {
    return wishlist.items().filter(function (item) {
      return HANDLE_RE.test(item.handle);
    }).map(function (item) {
      return {
        handle: item.handle,
        productId: item.productId,
        variantId: item.variantId == null ? null : item.variantId,
        color: item.color || null,
        addedAt: item.addedAt,
        price: item.price == null ? null : item.price
      };
    });
  }

  // Vereinigung nach handle: bei Dubletten gewinnt der Eintrag mit variantId,
  // sonst der neuere addedAt (Regel aus der Schnittstellen-Doku)
  function merge(local, remote) {
    var result = local.slice();
    var index = Object.create(null); // ohne Prototyp: ein Handle „constructor" darf nichts erben
    result.forEach(function (item, n) {
      index[item.handle] = n;
    });
    (Array.isArray(remote) ? remote : []).forEach(function (item) {
      if (!item || typeof item.handle !== 'string') {
        return;
      }
      var n = index[item.handle];
      if (n == null) {
        index[item.handle] = result.length;
        result.push(item);
        return;
      }
      var mine = result[n];
      var remoteWins;
      if ((mine.variantId == null) !== (item.variantId == null)) {
        remoteWins = item.variantId != null;
      } else {
        remoteWins = String(item.addedAt || '') > String(mine.addedAt || '');
      }
      if (remoteWins) {
        result[n] = item;
      }
    });
    return result;
  }

  /*---------- Verbindung ----------*/
  var pushTimer = null;
  var pushing = null;
  var pushAgain = false;

  function schedulePush() {
    if (!connected()) {
      return;
    }
    clearTimeout(pushTimer);
    pushTimer = setTimeout(push, PUSH_DELAY);
  }

  function push() {
    clearTimeout(pushTimer);
    if (!connected()) {
      return Promise.resolve(false);
    }
    if (pushing) {
      pushAgain = true;
      return pushing;
    }
    pushing = request('PUT', '/liste', { liste: exportList() }, true).then(function (result) {
      pushing = null;
      if (result.status === 401) {
        pushAgain = false;
        disconnect('expired');
        return false;
      }
      if (pushAgain) {
        pushAgain = false;
        return push();
      }
      return result.ok;
    }, function () {
      pushing = null;
      pushAgain = false;
      return false; // Netz weg: die naechste Aenderung versucht es wieder
    });
    return pushing;
  }

  // Antwort von GET /liste: Serverliste einarbeiten, Schluessel merken, Ergebnis zurueckschicken
  function connect(data) {
    var merged = merge(wishlist.items(), data.liste);
    setStored(DEVICE_KEY, String(data.geraet));
    setStored(EMAIL_KEY, data.email ? String(data.email) : '');
    wishlist.replace(merged);
    emitConnection(true);
    return push();
  }

  function disconnect(reason) {
    clearTimeout(pushTimer);
    setStored(DEVICE_KEY, null);
    setStored(EMAIL_KEY, null);
    if (reason === 'deleted') {
      setStored(KLAVIYO_FLAG, null); // Erinnerung abbestellen geht ueber den Link in der Mail
      setStored(ADDRESS_KEY, null);
    }
    emitConnection(false, reason);
  }

  function save(address, remind) {
    return request('POST', '/speichern', {
      email: address,
      liste: exportList(),
      erinnern: !!remind,
      sprache: locale()
    });
  }

  function requestLink(address) {
    return request('POST', '/link', { email: address, sprache: locale() });
  }

  function fetchByLink(key) {
    return request('GET', '/liste?abruf=' + encodeURIComponent(key));
  }

  // Angemeldeter Kunde: Shopify haengt logged_in_customer_id selbst an
  function fetchByCustomer() {
    return request('GET', '/liste');
  }

  function deleteOnServer() {
    return request('DELETE', '/liste', undefined, true).then(function (result) {
      if (result.ok || result.status === 401) {
        disconnect('deleted');
        return true;
      }
      return false;
    }, function () {
      return false;
    });
  }

  // Link aus der Mail: ?abruf=<schluessel>, einmal gueltig. Der Schluessel kommt
  // sofort aus der Adresszeile, vor dem Abruf – was danach laedt (Tracker, Pixel),
  // sieht ihn nicht mehr
  function handleLinkParam() {
    var match = /[?&]abruf=([^&#]+)/.exec(window.location.search);
    if (!match) {
      return Promise.resolve(null);
    }
    var key;
    try {
      key = decodeURIComponent(match[1]);
    } catch (e) {
      key = match[1]; // kaputte Kodierung: Rohwert, der Server lehnt ihn dann ab
    }

    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('abruf');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (e) {
      // dann bleibt der Parameter stehen
    }

    // Der Schluessel ist schon aus der Adresszeile, ein Neuladen hilft nicht
    // mehr – darum bei Netzfehler einmal von selbst wiederholen
    var tries = 0;
    function attempt() {
      tries += 1;
      return fetchByLink(key).then(function (result) {
        if (result.ok && result.data && result.data.geraet) {
          return connect(result.data).then(function () {
            wishlist.toast(t('fetched', 'Your saved list is now on this device.'));
            return true;
          });
        }
        wishlist.toast(t('linkInvalid', 'This link is invalid or has expired.'));
        return false;
      }, function () {
        if (tries < 2) {
          return new Promise(function (resolve) {
            setTimeout(resolve, LINK_RETRY_DELAY);
          }).then(attempt);
        }
        wishlist.toast(t('saveError', 'That did not work. Please try again.'));
        return false;
      });
    }
    return attempt();
  }

  document.addEventListener('wishlist:changed', function (evt) {
    var action = evt.detail && evt.detail.action;
    if (action === 'sync') {
      return; // anderer Tab desselben Browsers, der schickt selbst
    }
    schedulePush();
  });

  theme.WishlistSync = {
    connected: connected,
    email: email,
    isEmail: isEmail,
    merge: merge,
    exportList: exportList,
    save: save,
    requestLink: requestLink,
    fetchByLink: fetchByLink,
    fetchByCustomer: fetchByCustomer,
    connect: connect,
    disconnect: disconnect,
    push: push,
    deleteOnServer: deleteOnServer,
    handleLinkParam: handleLinkParam,
    klaviyoConfig: klaviyoConfig,
    klaviyoItems: klaviyoItems,
    klaviyoSubscribe: klaviyoSubscribe,
    activateReminder: activateReminder,
    reminderActive: reminderActive
  };

  /*---------- Formular „Liste speichern" auf der Wunschlisten-Seite ----------*/
  /*---------- Erinnerung ueber Klaviyo (Stufe 3) ----------
     Bei jedem erfolgreichen Speichern, wenn der Hinweis ueber dem Knopf zu
     sehen war (kein Haekchen, Daniel und David, 02.11.2026): Adresse an
     Klaviyos Client-API fuer die Liste melden (oeffentlicher
     Company-Schluessel, kein Geheimnis im Browser), Sprache und Artikel als
     Profil-Eigenschaften mitgeben, Browser identifizieren. Danach haelt dieser
     Browser die Artikel am Profil aktuell. Opt-in-Art und Flow stellt Klaviyo
     ein (seit 02.11.2026 Single Opt-in; die Einwilligung gilt fuers ganze
     Profil, darum nennt der Hinweis auch den Newsletter). Shop-Tools bleibt
     aussen vor. */
  var KLAVIYO_FLAG = 'acl_wishlist_erinnerung';
  // Wie acl-restock.js; 2024-10-15 ist seit 15.10.2026 ausgelaufen, und erst
  // die aktuelle Revision belegt `locale` am Profil der Anmeldung
  var KLAVIYO_REVISION = '2026-10-15';
  var KLAVIYO_MAX_ITEMS = 20;
  var klaviyoTimer = null;

  // Listen-ID aus der Section-Einstellung, Company-ID aus der Einstellung oder
  // aus der Klaviyo-Einbettung der Seite (static.klaviyo.com/onsite/js/<id>/…)
  function klaviyoConfig() {
    var el = document.querySelector('[data-wishlist-save]');
    var list = el ? String(el.getAttribute('data-klaviyo-list') || '').trim() : '';
    var company = el ? String(el.getAttribute('data-klaviyo-company') || '').trim() : '';
    if (!company) {
      var script = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
      var src = script ? String(script.src || script.getAttribute('src') || '') : '';
      var match = /company_id=([A-Za-z0-9]+)/.exec(src) || /onsite\/js\/([A-Za-z0-9]+)\//.exec(src);
      company = match ? match[1] : '';
    }
    return { list: list, company: company };
  }

  // Produkt-Helfer des Seiten-Moduls (wishlist.util); ohne sie nur das Noetigste
  function util() {
    return wishlist.util || {
      rootUrl: function () { return '/'; },
      fetchProduct: function () { return Promise.reject(new Error('keine Produkt-Helfer')); },
      formatMoney: function (cents) { return String(cents / 100); },
      imageUrl: function (src) { return src; }
    };
  }

  function absoluteUrl(path) {
    try {
      return new URL(path, window.location.href).href;
    } catch (e) {
      return path;
    }
  }

  function wishlistPageUrl() {
    var link = document.querySelector('[data-wishlist-link]');
    var href = link ? link.getAttribute('href') : '';
    return absoluteUrl(href || (util().rootUrl() + 'pages/wishlist'));
  }

  // Artikel fuer die Erinnerungsmail: Titel, Link, Bild, Preis, Farbe, Variante
  function klaviyoItems() {
    var entries = wishlist.items().slice(0, KLAVIYO_MAX_ITEMS);
    var base = util().rootUrl() + 'products/';
    return Promise.all(entries.map(function (entry) {
      var url = absoluteUrl(base + entry.handle);
      var fallback = {
        handle: entry.handle,
        title: entry.handle,
        url: url,
        image: '',
        price: entry.price == null ? '' : util().formatMoney(entry.price),
        price_cents: entry.price == null ? null : entry.price,
        color: entry.color || '',
        variant: ''
      };
      return util().fetchProduct(entry.handle).then(function (product) {
        var variants = product.variants || [];
        var variant = entry.variantId ? variants.filter(function (v) { return v.id === entry.variantId; })[0] || null : null;
        var cents = variant ? variant.price : null;
        if (cents == null && variants.length) {
          cents = Math.min.apply(null, variants.map(function (v) { return v.price; }));
        }
        var image = (variant && variant.image) || product.image;
        return {
          handle: entry.handle,
          title: product.title || entry.handle,
          url: url,
          image: image && image.src ? util().imageUrl(image.src, 600) : '',
          price: cents == null ? '' : util().formatMoney(cents),
          price_cents: cents == null ? null : cents,
          color: entry.color || '',
          variant: variant && variant.options ? variant.options.join(' / ') : ''
        };
      }).catch(function () {
        return fallback;
      });
    }));
  }

  function klaviyoProfile(items) {
    return {
      sprache: locale(),
      wishlist_count: items.length,
      wishlist_items: items,
      wishlist_url: wishlistPageUrl(),
      wishlist_updated_at: new Date().toISOString()
    };
  }

  // Profilfeld `locale` fuer Klaviyos Uebersetzungen (Mails und Seiten der
  // Liste, Flows lesen person.locale): nur die zwei Buchstaben der
  // Sprache. Mit Land waere z. B. de-AT eine eigene Sprache ohne Uebersetzung,
  // Klaviyo fiele auf Englisch zurueck. `sprache` bleibt daneben unveraendert.
  function klaviyoLocale() {
    return locale().slice(0, 2).toLowerCase();
  }

  // Klaviyo Client-API: Abonnement fuer die Liste. `locale` steckt in diesem
  // Aufruf, damit alles, was Klaviyo daraufhin verschickt, die Sprache kennt.
  function klaviyoSubscribe(address, properties) {
    var cfg = klaviyoConfig();
    if (!cfg.list || !cfg.company) {
      return Promise.resolve(false);
    }
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Wunschliste',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: address,
                locale: klaviyoLocale(),
                properties: properties,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: cfg.list } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (response) {
      return !!response.ok;
    }).catch(function () {
      return false;
    });
  }

  // Onsite-Objekt der Klaviyo-App, falls eingebettet
  function klaviyoOnsite() {
    var k = window.klaviyo;
    return k && typeof k.identify === 'function' ? k : null;
  }

  function klaviyoIdentified(k) {
    try {
      var result = k.isIdentified();
      return result && typeof result.then === 'function' ? result : Promise.resolve(result === true);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  // Artikel am identifizierten Profil nachziehen
  function klaviyoUpdateProfile() {
    var k = klaviyoOnsite();
    if (!k || typeof k.isIdentified !== 'function') {
      return Promise.resolve(false);
    }
    return klaviyoIdentified(k).then(function (identified) {
      if (!identified) {
        return false;
      }
      return klaviyoItems().then(function (items) {
        return k.identify(klaviyoProfile(items));
      }).then(function () {
        return true;
      });
    }).catch(function () {
      return false;
    });
  }

  // Liste geloescht: das identifizierte Profil weiss sonst noch von den alten
  // Artikeln und die zweite Erinnerung nennt sie. Nur der Browser, den Klaviyo
  // kennt; die Erinnerungen selbst enden ueber den Abmeldelink in der Mail.
  function klaviyoClearProfile() {
    var k = klaviyoOnsite();
    if (!k || typeof k.isIdentified !== 'function') {
      return Promise.resolve(false);
    }
    return klaviyoIdentified(k).then(function (identified) {
      if (!identified) {
        return false;
      }
      return Promise.resolve(k.identify({
        wishlist_count: 0,
        wishlist_items: [],
        wishlist_updated_at: new Date().toISOString()
      })).then(function () {
        return true;
      });
    }).catch(function () {
      return false;
    });
  }

  // Liste gespeichert: anmelden, merken, Browser identifizieren
  function activateReminder(address) {
    return klaviyoItems().then(function (items) {
      return klaviyoSubscribe(address, klaviyoProfile(items));
    }).then(function (ok) {
      if (!ok) {
        return false;
      }
      setStored(KLAVIYO_FLAG, '1');
      var k = klaviyoOnsite();
      if (k) {
        try {
          var done = k.identify({ $email: address, sprache: locale() });
          if (done && typeof done.then === 'function') {
            done.then(function () {
              if (typeof k.track === 'function') {
                k.track('Wishlist Saved', { Count: wishlist.count(), Language: locale() });
              }
            }).catch(function () {});
          }
        } catch (e) {
          // Klaviyo ist Beiwerk, das Speichern ist schon durch
        }
      }
      return true;
    }).catch(function () {
      return false;
    });
  }

  function reminderActive() {
    return getStored(KLAVIYO_FLAG) === '1';
  }

  // Liste geaendert: Artikel am Klaviyo-Profil nachziehen, gebuendelt
  document.addEventListener('wishlist:changed', function () {
    if (!reminderActive() || !klaviyoOnsite()) {
      return;
    }
    clearTimeout(klaviyoTimer);
    klaviyoTimer = setTimeout(klaviyoUpdateProfile, 1500);
  });

  /* Bestaetigung „Liste loeschen": das Theme-Modal aus sections/wishlist.liquid
     ([data-wishlist-confirm], theme.Modals mit Schleier, ESC, Fokusfalle);
     ohne Modal oder Theme-Code bleibt window.confirm. Liefert ein Promise
     mit true (Knopf „Liste loeschen") oder false (Abbrechen, Kreuz, ESC,
     Klick daneben) */
  var confirmModal = null;

  function confirmDelete() {
    var el = document.querySelector('[data-wishlist-confirm]');
    if (!el || !el.id || !theme.Modals) {
      return Promise.resolve(window.confirm(t('deleteConfirm', 'Delete your saved list from the server?')));
    }
    if (!confirmModal || confirmModal.modal !== el) {
      var cancel = el.querySelector('[data-confirm-cancel]');
      confirmModal = new theme.Modals(el.id, 'wishlist-confirm', {
        focusIdOnOpen: cancel && cancel.id ? cancel.id : null
      });
      if (!confirmModal.modal) {
        confirmModal = null;
        return Promise.resolve(window.confirm(t('deleteConfirm', 'Delete your saved list from the server?')));
      }
    }
    var modal = confirmModal;
    return new Promise(function (resolve) {
      var ok = el.querySelector('[data-confirm-ok]');
      var closeEvent = 'modalClose.' + el.id;
      var settled = false;

      function finish(result) {
        if (settled) {
          return;
        }
        settled = true;
        if (ok) {
          ok.removeEventListener('click', onOk);
        }
        document.removeEventListener(closeEvent, onClose);
        resolve(result);
      }
      function onOk() {
        finish(true);
        modal.close();
      }
      function onClose() {
        finish(false);
      }

      if (ok) {
        ok.addEventListener('click', onOk);
      }
      document.addEventListener(closeEvent, onClose);
      modal.open();
    });
  }

  function SaveBox(el) {
    this.el = el;
    this.state = 'start'; // start | form | sent
    this.mode = 'save';   // save | link
    this.busy = false;
    this.start = this.q('start');
    this.openSave = this.q('open-save');
    this.openLink = this.q('open-link');
    this.form = this.q('form');
    this.intro = this.q('intro');
    this.emailInput = this.q('email');
    this.note = this.q('note'); // Hinweis Erinnerung und Newsletter, nur mit Klaviyo
    this.privacySave = this.q('privacy-save');
    this.privacyLink = this.q('privacy-link');
    this.submit = this.q('submit');
    this.cancel = this.q('cancel');
    this.error = this.q('error');
    this.sent = this.q('sent');
    this.sentRemind = this.q('sent-remind');
    this.sentOk = this.q('sent-ok');
    this.remindSent = false;
    this.connectedEl = this.q('connected');
    this.connectedText = this.q('connected-text');
    this.deleteButton = this.q('delete');
    this.accountHint = this.q('account-hint'); // nur fuer angemeldete Kunden im Markup
    this.accountUnlinked = false; // Abruf zum Konto hat 404 geliefert

    var self = this;
    this.onChanged = function () {
      self.render();
    };
    this.onConnection = function (evt) {
      self.state = 'start';
      self.accountUnlinked = false;
      self.render();
      if (evt.detail && evt.detail.reason === 'expired') {
        wishlist.toast(t('disconnected', 'The connection to your saved list has expired.'));
      }
    };
    document.addEventListener('wishlist:changed', this.onChanged);
    document.addEventListener('wishlist:connection', this.onConnection);

    if (this.openSave) {
      this.openSave.addEventListener('click', function () { self.open('save'); });
    }
    if (this.openLink) {
      this.openLink.addEventListener('click', function () { self.open('link'); });
    }
    if (this.form) {
      this.form.addEventListener('submit', function (evt) { self.onSubmit(evt); });
    }
    if (this.cancel) {
      this.cancel.addEventListener('click', function () { self.state = 'start'; self.render(); });
    }
    if (this.sentOk) {
      this.sentOk.addEventListener('click', function () { self.state = 'start'; self.render(); });
    }
    if (this.deleteButton) {
      this.deleteButton.addEventListener('click', function () { self.onDelete(); });
    }

    this.render();

    // Angemeldeter Kunde ohne Verbindung: die Liste zum Konto einmal versuchen
    if (el.dataset.customer && !connected()) {
      el.classList.add('is-checking');
      fetchByCustomer().then(function (result) {
        if (result.ok && result.data && result.data.geraet) {
          return connect(result.data);
        }
        // Keine Liste am Konto: der Link muss einmal angemeldet geoeffnet werden
        self.accountUnlinked = result.status === 404;
        return null;
      }).catch(function () {
        return null;
      }).then(function () {
        el.classList.remove('is-checking');
        self.render();
      });
    }
  }

  SaveBox.prototype = {
    q: function (name) {
      return this.el.querySelector('[data-save-' + name + ']');
    },

    show: function (node, visible) {
      if (node) {
        node.hidden = !visible;
      }
    },

    open: function (mode) {
      this.mode = mode;
      this.state = 'form';
      this.render();
      if (this.emailInput) {
        // Angemeldete Kunden bringt die Vorlage schon mit (customer.email)
        if (!this.emailInput.value) {
          this.emailInput.value = suggestedAddress();
        }
        this.emailInput.focus();
      }
    },

    render: function () {
      var isConnected = connected();
      var hasItems = wishlist.count() > 0;
      this.show(this.connectedEl, isConnected);
      this.show(this.start, !isConnected && this.state === 'start');
      this.show(this.form, !isConnected && this.state === 'form');
      this.show(this.sent, !isConnected && this.state === 'sent');
      this.show(this.sentRemind, !isConnected && this.state === 'sent' && this.remindSent);
      this.show(this.accountHint, !isConnected && this.state === 'start' && this.accountUnlinked);
      if (isConnected && this.connectedText) {
        var pattern = this.connectedText.dataset.text || '[email]';
        this.connectedText.textContent = pattern.replace('[email]', email());
      }
      this.show(this.openSave, hasItems);
      if (this.intro) {
        this.intro.textContent = this.mode === 'link' ? (this.intro.dataset.textLink || '') : (this.intro.dataset.textSave || '');
      }
      // „Gespeicherte Liste" (Link erneut senden) meldet nicht an
      this.show(this.note, this.mode === 'save');
      this.show(this.privacySave, this.mode === 'save');
      this.show(this.privacyLink, this.mode === 'link');
      if (this.state !== 'form') {
        this.showError('');
      }
    },

    showError: function (text) {
      if (!this.error) {
        return;
      }
      this.error.textContent = text || '';
      this.error.hidden = !text;
    },

    onSubmit: function (evt) {
      evt.preventDefault();
      if (this.busy) {
        return;
      }
      var self = this;
      var address = this.emailInput ? this.emailInput.value.trim() : '';
      if (!isEmail(address)) {
        this.showError(t('emailInvalid', 'Please enter a valid email address.'));
        if (this.emailInput) {
          this.emailInput.focus();
        }
        return;
      }
      this.busy = true;
      this.showError('');
      if (this.submit) {
        this.submit.classList.add('btn--loading');
        this.submit.disabled = true;
      }
      // Anmelden nur beim Speichern und nur, wenn der Hinweis zu sehen war
      // (auch nicht per Custom-CSS weggeblendet)
      var remind = this.mode === 'save' && visible(this.note);
      var call = this.mode === 'link' ? requestLink(address) : save(address, remind);
      call.then(function (result) {
        self.done();
        if (result.ok) {
          setStored(ADDRESS_KEY, address);
          self.remindSent = false;
          self.state = 'sent';
          self.render();
          if (remind) {
            // Klaviyo-Anmeldung laeuft nebenher, die Liste ist schon gespeichert;
            // „auch fuer Erinnerungen und Newsletter angemeldet" erst, wenn
            // Klaviyo angenommen hat – sonst bleibt der neutrale Text
            activateReminder(address).then(function (ok) {
              self.remindSent = ok === true;
              self.render();
            });
          }
          return;
        }
        if (result.status === 422) {
          self.showError(t('emailInvalid', 'Please enter a valid email address.'));
        } else if (result.status === 429) {
          self.showError(t('tooMany', 'Too many attempts. Please wait a few minutes.'));
        } else {
          self.showError(t('saveError', 'That did not work. Please try again.'));
        }
      }, function () {
        self.done();
        self.showError(t('saveError', 'That did not work. Please try again.'));
      });
    },

    done: function () {
      this.busy = false;
      if (this.submit) {
        this.submit.classList.remove('btn--loading');
        this.submit.disabled = false;
      }
    },

    onDelete: function () {
      var self = this;
      if (this.busy) {
        return;
      }
      confirmDelete().then(function (yes) {
        if (!yes || self.busy) {
          return;
        }
        self.busy = true;
        deleteOnServer().then(function (ok) {
          self.busy = false;
          if (!ok) {
            wishlist.toast(t('saveError', 'That did not work. Please try again.'));
          } else {
            klaviyoClearProfile(); // Beiwerk, laeuft nebenher
          }
          self.render();
        });
      });
    },

    destroy: function () {
      document.removeEventListener('wishlist:changed', this.onChanged);
      document.removeEventListener('wishlist:connection', this.onConnection);
    }
  };

  function initBoxes(root) {
    (root || document).querySelectorAll('[data-wishlist-save]').forEach(function (el) {
      if (!el.wishlistSaveBox) {
        el.wishlistSaveBox = new SaveBox(el);
      }
    });
  }

  // Theme-Editor: Section neu geladen oder entfernt
  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target && evt.target.querySelectorAll) {
      initBoxes(evt.target);
    }
  });
  document.addEventListener('shopify:section:unload', function (evt) {
    if (!evt.target || !evt.target.querySelectorAll) {
      return;
    }
    evt.target.querySelectorAll('[data-wishlist-save]').forEach(function (el) {
      if (el.wishlistSaveBox) {
        el.wishlistSaveBox.destroy();
        el.wishlistSaveBox = null;
      }
    });
  });

  function init() {
    initBoxes(document);
    handleLinkParam();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
